/**
 * Swiper Element (https://swiperjs.com/element) for resource-list carousels.
 * Desktop: columns + 0.2 visible (e.g. 4.2). Mobile: 2 items (configurable via data-mobile-spv).
 * Pagination: one step = 1 slide (slidesPerGroup: 1); dots are clickable.
 * data-page-pagination="true" uses one dot per page of fully visible slides.
 */
(function () {
  var BP = 1024;

  function parseNum(v, fallback) {
    var n = parseFloat(String(v), 10);
    return Number.isFinite(n) ? n : fallback;
  }

  /**
   * Swiper Element uses shadow DOM; --ari-nc-slide-width on the wrapper often does not apply to swiper-slide.
   * Set width in px on each slide + keep CSS var for fallback. Breakpoint uses innerWidth (same window as preview iframe).
   */
  function bindFluidSlideWidth(container) {
    if (container.getAttribute('data-sync-slide-width') !== 'true') return;
    var wrapper = container.closest('.ari-swiper-carousel__wrapper');
    if (!wrapper) return;

    var gap = parseNum(container.getAttribute('data-gap'), 16);
    var mobileSpv = parseNum(container.getAttribute('data-mobile-spv'), 2.2);
    var columnCount = parseNum(container.getAttribute('data-column-count'), 4);
    var desktopSpv = columnCount + 0.2;
    var bp = parseNum(container.getAttribute('data-slide-spv-bp'), 1024);

    var win = (container.ownerDocument && container.ownerDocument.defaultView) || window;
    var doc = win.document || document;
    var rafId = 0;

    function viewportWidth() {
      return win.innerWidth || (doc.documentElement && doc.documentElement.clientWidth) || 0;
    }

    function isWideViewport() {
      return viewportWidth() >= bp;
    }

    function updateSwiper() {
      var inst = container.swiper;
      if (inst && typeof inst.update === 'function') inst.update();
    }

    function applyWidthsToSlides(slidePx) {
      var px = slidePx.toFixed(2) + 'px';
      container.querySelectorAll('swiper-slide').forEach(function (slide) {
        slide.style.setProperty('width', px, 'important');
        slide.style.setProperty('max-width', px, 'important');
        slide.style.setProperty('min-width', px, 'important');
      });
    }

    function applySlideWidth() {
      var trackW = container.getBoundingClientRect().width;
      if (trackW < 1) trackW = wrapper.getBoundingClientRect().width;
      if (trackW < 1) return;

      var spv = isWideViewport() ? desktopSpv : mobileSpv;
      var slidePx = Math.max(0, (trackW - (spv - 1) * gap) / spv);
      wrapper.style.setProperty('--ari-nc-slide-width', slidePx + 'px');
      container.style.setProperty('--ari-nc-slide-width', slidePx + 'px');
      applyWidthsToSlides(slidePx);

      requestAnimationFrame(function () {
        updateSwiper();
        applyWidthsToSlides(slidePx);
        requestAnimationFrame(function () {
          updateSwiper();
          applyWidthsToSlides(slidePx);
        });
      });
    }

    function scheduleApply() {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(applySlideWidth);
    }

    if (container.getAttribute('data-ari-fluid-slide-bound') !== 'true') {
      container.setAttribute('data-ari-fluid-slide-bound', 'true');

      if (typeof ResizeObserver !== 'undefined') {
        var ro = new ResizeObserver(scheduleApply);
        ro.observe(wrapper);
        ro.observe(container);
      }
      win.addEventListener('resize', scheduleApply);
      if (typeof win.matchMedia === 'function') {
        var mql = win.matchMedia('(min-width: ' + bp + 'px)');
        if (mql.addEventListener) mql.addEventListener('change', scheduleApply);
        else if (mql.addListener) mql.addListener(scheduleApply);
      }
    }

    applySlideWidth();
    var tries = 0;
    function retryUntilSwiper() {
      updateSwiper();
      if ((!container.swiper || typeof container.swiper.update !== 'function') && tries < 12) {
        tries += 1;
        requestAnimationFrame(retryUntilSwiper);
      }
    }
    requestAnimationFrame(retryUntilSwiper);
  }

  function fullyVisibleCount(container) {
    var columnCount = parseNum(container.getAttribute('data-column-count'), 4);
    var mobileSpv = parseNum(container.getAttribute('data-mobile-spv'), 2.2);
    var bp = parseNum(container.getAttribute('data-slide-spv-bp'), 1024);
    var win = (container.ownerDocument && container.ownerDocument.defaultView) || window;
    var wide = (win.innerWidth || 0) >= bp;
    return Math.max(1, Math.floor(wide ? columnCount : mobileSpv));
  }

  function bindPagePagination(container) {
    if (container.getAttribute('data-page-pagination') !== 'true') return;
    if (container.getAttribute('data-ari-page-pagination-bound') === 'true') return;
    container.setAttribute('data-ari-page-pagination-bound', 'true');

    var refreshing = false;
    var pageLabel = container.getAttribute('data-page-label') || 'Go to page';

    function refresh() {
      if (refreshing) return;
      var swiper = container.swiper;
      if (!swiper) return;
      refreshing = true;
      var size = fullyVisibleCount(container);
      var total = container.querySelectorAll('swiper-slide').length;
      var pages = total <= size ? 1 : Math.ceil(total / size);
      var panel = container.closest('[data-swiper-dots-root]');
      if (panel) panel.classList.toggle('is-single-page', pages <= 1);
      if (swiper.params.slidesPerGroup !== size) {
        swiper.params.slidesPerGroup = size;
        swiper.update();
      }

      var dotsEl = panel && panel.querySelector('[data-carousel-dots]');
      var showDots = container.getAttribute('data-show-dots') !== 'false';
      if (dotsEl) {
        if (pages <= 1 || !showDots) {
          dotsEl.innerHTML = '';
        } else if (dotsEl.childElementCount !== pages) {
          dotsEl.innerHTML = '';
          for (var i = 0; i < pages; i++) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'swiper-pagination-bullet';
            btn.setAttribute('aria-label', pageLabel + ' ' + (i + 1));
            btn.dataset.page = String(i);
            btn.addEventListener('click', function () {
              var page = parseInt(this.dataset.page, 10);
              var step = fullyVisibleCount(container);
              if (container.swiper) container.swiper.slideTo(page * step);
            });
            dotsEl.appendChild(btn);
          }
        }
        if (dotsEl.childElementCount) {
          var activePage = Math.min(
            dotsEl.childElementCount - 1,
            Math.max(0, Math.floor((swiper.activeIndex || 0) / size))
          );
          Array.prototype.forEach.call(dotsEl.children, function (el, idx) {
            var isActive = idx === activePage;
            el.classList.toggle('swiper-pagination-bullet-active', isActive);
            if (isActive) el.setAttribute('aria-current', 'true');
            else el.removeAttribute('aria-current');
          });
        }
      }
      refreshing = false;
    }

    var swiper = container.swiper;
    if (swiper) {
      swiper.on('slideChange', refresh);
      swiper.on('resize', refresh);
    }
    var win = (container.ownerDocument && container.ownerDocument.defaultView) || window;
    win.addEventListener('resize', refresh);
    refresh();
  }

  function initContainer(container) {
    if (!container || container.getAttribute('data-ari-swiper-initialized') === 'true') return;

    var slides = container.querySelectorAll('swiper-slide');
    if (!slides.length) return;

    var root =
      container.closest('[data-ari-swiper-root]') ||
      container.closest('[data-ari-resource-carousel]') ||
      container.closest('.resource-list__carousel') ||
      document;

    var gap = parseNum(container.getAttribute('data-gap'), 16);
    var columnCount = parseNum(container.getAttribute('data-column-count'), 4);
    var desktopSpv = columnCount + 0.2;
    var mobileSpv = parseNum(container.getAttribute('data-mobile-spv'), 2);
    var mobileRows = Math.max(1, parseNum(container.getAttribute('data-mobile-rows'), 1));
    var mobileGroup = Math.max(1, parseNum(container.getAttribute('data-mobile-group'), 1));
    var showNav = container.getAttribute('data-show-nav') === 'true';
    var autoplayEnabled = container.getAttribute('data-autoplay') === 'true';
    var autoplayDelay = parseNum(container.getAttribute('data-autoplay-delay'), 4000);
    var keyboardEnabled = container.getAttribute('data-keyboard') !== 'false';

    var navDisabled = container.getAttribute('data-carousel-nav-disabled') === 'true';
    var navScope = container.closest('[data-swiper-nav-root]');
    var prevBtn = null;
    var nextBtn = null;
    if (!navDisabled) {
      if (navScope) {
        prevBtn = navScope.querySelector('[data-carousel-prev]');
        nextBtn = navScope.querySelector('[data-carousel-next]');
      } else {
        prevBtn = root.querySelector('[data-carousel-prev]');
        nextBtn = root.querySelector('[data-carousel-next]');
      }
    }

    var dotsScope = container.closest('[data-swiper-dots-root]');
    var dotsEl = dotsScope
      ? dotsScope.querySelector('[data-carousel-dots]')
      : root.querySelector('[data-carousel-dots]');

    var slidesOffsetAfter = container.getAttribute('data-slides-offset-after');
    var offsetAfter =
      slidesOffsetAfter === null || slidesOffsetAfter === ''
        ? null
        : parseNum(slidesOffsetAfter, 0);

    /**
     * Fixed slide widths via CSS + slidesPerView: 'auto' keeps snap / pagination aligned.
     * Fractional numeric slidesPerView (e.g. 4.2) often drifts after the first snap (wrong translate).
     */
    var useAutoSlides = container.getAttribute('data-auto-slides') === 'true';
    var singlePage = container.getAttribute('data-swiper-single-page') === 'true';
    var pagePagination = container.getAttribute('data-page-pagination') === 'true';
    var pageGroup = pagePagination ? fullyVisibleCount(container) : 1;

    var params;

    if (singlePage) {
      params = {
        init: false,
        spaceBetween: 0,
        slidesPerView: 1,
        slidesPerGroup: 1,
        slidesPerGroupAuto: false,
        slidesPerGroupSkip: 0,
        speed: 400,
        watchOverflow: true,
        grabCursor: true,
        centeredSlides: false,
        observer: true,
        observeParents: true,
        keyboard: { enabled: keyboardEnabled, onlyInViewport: true },
      };
    } else if (useAutoSlides) {
      params = {
        init: false,
        spaceBetween: gap,
        slidesPerView: 'auto',
        slidesPerGroup: pageGroup,
        slidesPerGroupAuto: false,
        slidesPerGroupSkip: 0,
        speed: 400,
        watchOverflow: true,
        grabCursor: true,
        centeredSlides: false,
        observer: true,
        observeParents: true,
        keyboard: { enabled: keyboardEnabled, onlyInViewport: true },
      };
    } else {
      params = {
        init: false,
        spaceBetween: gap,
        slidesPerGroup: 1,
        speed: 400,
        watchOverflow: true,
        grabCursor: true,
        roundLengths: true,
        observer: true,
        observeParents: true,
        watchSlidesProgress: true,
        keyboard: { enabled: keyboardEnabled, onlyInViewport: true },
        breakpoints: {
          0: {
            slidesPerView: mobileSpv,
            slidesPerGroup: mobileGroup,
            grid: {
              rows: mobileRows,
              fill: 'row',
            },
          },
          [BP]: {
            slidesPerView: desktopSpv,
            slidesPerGroup: 1,
            grid: {
              rows: 1,
            },
          },
        },
      };
    }

    if (dotsEl && !pagePagination) {
      params.pagination = {
        el: dotsEl,
        clickable: true,
        type: 'bullets',
      };
    }

    if (showNav && prevBtn && nextBtn) {
      params.navigation = {
        prevEl: prevBtn,
        nextEl: nextBtn,
      };
    }

    if (autoplayEnabled && autoplayDelay >= 0) {
      params.autoplay = {
        delay: autoplayDelay,
        disableOnInteraction: true,
        pauseOnMouseEnter: true,
      };
    }

    if (!useAutoSlides && offsetAfter !== null && offsetAfter > 0) {
      params.slidesOffsetAfter = offsetAfter;
    }

    try {
      Object.assign(container, params);
      container.initialize();
      container.setAttribute('data-ari-swiper-initialized', 'true');
      if (pagePagination) bindPagePagination(container);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          bindFluidSlideWidth(container);
        });
      });
    } catch (e) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[ari-swiper-carousel]', e);
      }
    }
  }

  function scheduleInitContainer(container) {
    if (!container) return;
    if (typeof customElements !== 'undefined' && customElements.whenDefined) {
      customElements.whenDefined('swiper-container').then(function () {
        initContainer(container);
      });
    } else {
      requestAnimationFrame(function () {
        initContainer(container);
      });
    }
  }

  function initInRoot(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('swiper-container.ari-swiper-carousel__container').forEach(function (el) {
      scheduleInitContainer(el);
    });
  }

  function onReady() {
    initInRoot(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initInRoot(e.target);
  });

  document.addEventListener('product-recommendations:loaded', function (e) {
    var t = e.target;
    if (t && t.querySelectorAll) initInRoot(t);
  });

  window.initAriSwiperCarousels = initInRoot;
})();
